import React, { useState } from 'react';
import { Badge, Button, Select, Space, Typography, message } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';

const { Text } = Typography;

export interface EpcEstimateBulkAssignBarProps {
  selectedIds: string[];
  totalCount: number;
  readOnly: boolean;
  wbsOptions: Array<{ id: string; code: string; name?: string }>;
  cbsOptions: Array<{ id: string; code: string; name?: string }>;
  boqOptions?: Array<{ id: string; code?: string; item_number?: string; name?: string }>;
  onClearSelection: () => void;
  onSelectAll?: () => void;
  onAssign: (patch: {
    wbsNodeId?: string;
    cbsCodeId?: string;
    clientBoqLineId?: string;
  }) => Promise<void>;
  pending?: boolean;
}

/**
 * EST3 UX2 Wave 9 — bulk WBS/CBS assign bar (visual pattern from BulkActionsBar).
 */
export const EpcEstimateBulkAssignBar: React.FC<EpcEstimateBulkAssignBarProps> = ({
  selectedIds,
  totalCount,
  readOnly,
  wbsOptions,
  cbsOptions,
  boqOptions = [],
  onClearSelection,
  onSelectAll,
  onAssign,
  pending,
}) => {
  const [wbsNodeId, setWbsNodeId] = useState<string | undefined>();
  const [cbsCodeId, setCbsCodeId] = useState<string | undefined>();
  const [clientBoqLineId, setClientBoqLineId] = useState<string | undefined>();

  if (!selectedIds.length || readOnly) return null;

  const apply = async () => {
    if (!wbsNodeId && !cbsCodeId && !clientBoqLineId) {
      message.warning('Select WBS, CBS, and/or BOQ to assign');
      return;
    }
    const patch: { wbsNodeId?: string; cbsCodeId?: string; clientBoqLineId?: string } = {};
    if (wbsNodeId) patch.wbsNodeId = wbsNodeId;
    if (cbsCodeId) patch.cbsCodeId = cbsCodeId;
    if (clientBoqLineId) patch.clientBoqLineId = clientBoqLineId;
    await onAssign(patch);
    setWbsNodeId(undefined);
    setCbsCodeId(undefined);
    setClientBoqLineId(undefined);
  };

  return (
    <div
      className="est3-bulk-assign-bar"
      style={{
        position: 'fixed',
        bottom: 'max(24px, env(safe-area-inset-bottom, 0px))',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1000,
        background: '#ffffff',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
        borderRadius: 8,
        padding: '12px 16px',
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 12,
        maxWidth: 'min(720px, calc(100vw - 24px))',
        justifyContent: 'center',
        boxSizing: 'border-box',
      }}
    >
      <Badge count={selectedIds.length} showZero>
        <CheckCircleOutlined style={{ fontSize: 20, color: '#145A54' }} />
      </Badge>
      <Text strong>
        {selectedIds.length} of {totalCount} selected
      </Text>
      <div style={{ borderLeft: '1px solid #d9d9d9', height: 24 }} />
      <Space wrap size="small">
        {onSelectAll && selectedIds.length < totalCount ? (
          <Button type="link" onClick={onSelectAll}>
            Select all ({totalCount})
          </Button>
        ) : null}
        <Select
          allowClear
          placeholder="WBS"
          style={{ width: 120 }}
          value={wbsNodeId}
          onChange={setWbsNodeId}
          options={wbsOptions.map((n) => ({ value: n.id, label: n.code }))}
        />
        <Select
          allowClear
          placeholder="CBS"
          style={{ width: 120 }}
          value={cbsCodeId}
          onChange={setCbsCodeId}
          options={cbsOptions.map((n) => ({ value: n.id, label: n.code }))}
        />
        <Select
          allowClear
          placeholder="BOQ"
          style={{ width: 120 }}
          value={clientBoqLineId}
          onChange={setClientBoqLineId}
          options={boqOptions.map((n) => ({
            value: n.id,
            label: (n as { item_number?: string; code?: string }).item_number || n.code,
          }))}
        />
        <Button type="primary" size="small" loading={pending} onClick={() => void apply()}>
          Assign
        </Button>
        <Button type="text" icon={<CloseCircleOutlined />} onClick={onClearSelection}>
          Clear
        </Button>
      </Space>
    </div>
  );
};
